// NOTIF-01 (VEK-117) — "my notifications in Telegram" panel at the top of /notifications, beside PushPanel.
// States: bot not configured on this server (not rendered); off — a Connect Telegram action that opens the
// connect dialog (QR, link, expiry, Show new code once expired; polls the status and closes when /start
// lands); on — a notice that notifications go to Telegram with a Disconnect action. Connecting requests no
// browser permission and registers no push subscription.
// NOTIF-06 — the dialog also shows the bare code with a Copy action: a chat opened any way can be bound by
// sending the code to the bot as a message (the bot reads a 22-character code like /start <code>).
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { QRCodeSVG } from 'qrcode.react';

import { ApiError } from 'api/vektra/client';
import { useCreateMyTelegramConnectCode, useDisconnectMyTelegram, useMyTelegramStatus } from 'api/vektra/hooks';
import { formatDateTime } from 'utils/date';

// How often the open dialog re-reads the link, so it closes soon after the operator presses Start.
const STATUS_POLL_MS = 3_000;

// Exported for NOTIF-02 (VEK-121): the drawer's Connect Telegram Bot row opens this very dialog.
export function ConnectDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const create = useCreateMyTelegramConnectCode();
  const { mutate: mint } = create;
  const { data: status } = useMyTelegramStatus(STATUS_POLL_MS);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    mint(undefined, {
      // A 400 means this user connected meanwhile (another tab, another device): refresh the panel behind.
      onError: () => queryClient.invalidateQueries({ queryKey: ['me', 'telegram'] })
    });
  }, [mint, queryClient]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (status?.connected) onClose();
  }, [status?.connected, onClose]);

  // "Copied" reads for two seconds, then the action offers Copy again.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2_000);
    return () => clearTimeout(timer);
  }, [copied]);

  const code = create.data;
  const startUrl = code?.startUrl ?? null;
  const bareCode = code?.code ?? null;

  const copyCode = async () => {
    if (!bareCode) return;
    try {
      await navigator.clipboard.writeText(bareCode);
      setCopied(true);
    } catch {
      // No clipboard (plain-HTTP origin, denied permission): the code stays selectable text.
    }
  };
  const expiresAt = code?.expiresAt ? new Date(code.expiresAt).getTime() : null;
  const expired = expiresAt !== null && now >= expiresAt;
  const error = create.error
    ? create.error instanceof ApiError
      ? create.error.serverMessage || create.error.message
      : String(create.error)
    : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-testid="my-telegram-connect-dialog">
      <DialogTitle>{t('notifications.telegram.dialog.title', { defaultValue: 'Connect Telegram' })}</DialogTitle>
      <DialogContent>
        {create.isPending && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        )}
        {error && (
          <Alert severity="error" data-testid="my-telegram-connect-error">
            {error}
          </Alert>
        )}
        {create.isSuccess && !startUrl && (
          <Alert severity="info">
            {t('notifications.telegram.dialog.botOff', { defaultValue: 'The staff Telegram bot is not configured on this server.' })}
          </Alert>
        )}
        {startUrl && expired && (
          <Stack spacing={2} alignItems="center" textAlign="center" data-testid="my-telegram-connect-expired">
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('notifications.telegram.dialog.expired', { defaultValue: 'This code has expired.' })}
            </Alert>
            <Button variant="contained" onClick={() => mint()} data-testid="my-telegram-connect-renew">
              {t('notifications.telegram.dialog.renew', { defaultValue: 'Show new code' })}
            </Button>
          </Stack>
        )}
        {startUrl && !expired && (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography color="text.secondary">
              {t('notifications.telegram.dialog.intro', {
                defaultValue:
                  'Scan the code with your phone camera or open the link and press Start in Telegram — or send the code below to the bot as a message.'
              })}
            </Typography>
            <Box sx={{ bgcolor: '#ffffff', p: 1, borderRadius: 1, lineHeight: 0 }} data-testid="my-telegram-connect-qr">
              <QRCodeSVG value={startUrl} size={220} level="L" marginSize={2} bgColor="#ffffff" fgColor="#000000" />
            </Box>
            <Button variant="outlined" href={startUrl} target="_blank" rel="noopener noreferrer" data-testid="my-telegram-connect-link">
              {t('notifications.telegram.dialog.open', { defaultValue: 'Open in Telegram' })}
            </Button>
            {bareCode && (
              <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }} data-testid="my-telegram-connect-code-block">
                <Typography variant="caption" color="text.secondary">
                  {t('notifications.telegram.dialog.codeHint', { defaultValue: 'Or send this code to the bot as a message:' })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    component="code"
                    sx={{ fontFamily: 'monospace', fontSize: 15, letterSpacing: 0.5, userSelect: 'all', wordBreak: 'break-all' }}
                    data-testid="my-telegram-connect-code"
                  >
                    {bareCode}
                  </Typography>
                  <Button size="small" onClick={copyCode} data-testid="my-telegram-connect-copy">
                    {copied
                      ? t('notifications.telegram.dialog.copied', { defaultValue: 'Copied' })
                      : t('notifications.telegram.dialog.copy', { defaultValue: 'Copy' })}
                  </Button>
                </Stack>
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" data-testid="my-telegram-connect-expires">
              {t('notifications.telegram.dialog.expires', { defaultValue: 'Valid until {{time}}', time: formatDateTime(code?.expiresAt) })}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TelegramPanel() {
  const { t } = useTranslation();
  const { data: status } = useMyTelegramStatus();
  const disconnect = useDisconnectMyTelegram();
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (!status?.botEnabled) return null;

  const onDisconnect = async () => {
    setFailed(false);
    try {
      await disconnect.mutateAsync();
    } catch {
      setFailed(true);
    }
  };

  return (
    <>
      {status.connected ? (
        <Alert
          severity="success"
          data-testid="telegram-panel-on"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={onDisconnect}
              disabled={disconnect.isPending}
              data-testid="telegram-panel-disconnect"
            >
              {t('notifications.telegram.disconnect', { defaultValue: 'Disconnect' })}
            </Button>
          }
        >
          {t('notifications.telegram.on', { defaultValue: 'Your notifications go to Telegram instead of browser push.' })}
        </Alert>
      ) : (
        <Alert
          severity="info"
          data-testid="telegram-panel-off"
          action={
            <Button color="primary" variant="contained" size="small" onClick={() => setOpen(true)} data-testid="telegram-panel-connect">
              {t('notifications.telegram.connect', { defaultValue: 'Connect Telegram' })}
            </Button>
          }
        >
          {t('notifications.telegram.off', {
            defaultValue: 'Get your notifications in Telegram — assignments arrive as a card you accept or reject in chat.'
          })}
        </Alert>
      )}
      {failed && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="telegram-panel-error">
          {t('notifications.telegram.failed', { defaultValue: 'Could not change the Telegram connection. Try again.' })}
        </Alert>
      )}
      {/* Rendered conditionally: a Dialog whose `open` flips before its portal mounts leaks the body scroll lock. */}
      {open && <ConnectDialog onClose={close} />}
    </>
  );
}
